/**
 * AiExplainer — an inline AI chat component for blog posts.
 *
 * Usage in MDX:
 *   <AiExplainer section="parameters" />
 *
 * Initially renders as an AI button + single-line input. On submit, expands
 * into a persistent inline chat (session-scoped) powered by OpenRouter.
 * Uses the same API key and model as the playground AI settings.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Sparkles, Send, X, Loader2, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAiSettings } from '@/hooks/useAiSettings';
import { createAiClient, streamChatCompletion } from '@/services/ai';
import type { ChatMessage } from '@/services/ai';
import { buildExplainerSystemPrompt } from '@/services/aiPrompts';
import { AiSettingsDialog } from '@/components/ai/AiSettingsDialog';
import { useBlogPost } from '@/blog/context/BlogPostContext';

export interface AiExplainerProps {
  /**
   * A short label for the section this explainer is placed below.
   * Passed to the LLM as context ("You are below the section: X").
   * Example: section="parameters" or section="boolean operations"
   */
  section: string;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
  /** True while the assistant message is still streaming */
  streaming?: boolean;
}

export function AiExplainer({ section }: AiExplainerProps) {
  const { rawContent, meta } = useBlogPost();

  const {
    settings,
    isConfigured,
    setApiKey,
    setDefaultModel,
    setPromptTemplate,
    resetPromptTemplates,
  } = useAiSettings();

  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(0);

  // Scroll to bottom only when a new message is appended (not on every
  // streaming chunk), so the user can scroll freely while the LLM responds.
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      prevMessageCountRef.current = messages.length;
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSubmit = useCallback(
    async (question: string) => {
      const q = question.trim();
      if (!q || isStreaming) return;

      if (!isConfigured) {
        setShowSettings(true);
        return;
      }

      setExpanded(true);
      setInputValue('');
      // Small delay so the animation completes before focusing
      setTimeout(() => inputRef.current?.focus(), 50);

      const userMsg: Message = { role: 'user', content: q };
      const assistantMsg: Message = {
        role: 'assistant',
        content: '',
        streaming: true,
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);

      // Build the full message history for the API call
      const systemPrompt = buildExplainerSystemPrompt(
        rawContent,
        section || meta.title,
      );

      const history: ChatMessage[] = [
        { role: 'system', content: systemPrompt },
        // Include previous conversation turns (exclude streaming placeholder)
        ...messages.map((m) => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
        })),
        { role: 'user', content: q },
      ];

      const abort = new AbortController();
      abortRef.current = abort;

      try {
        const client = createAiClient(settings.apiKey);
        await streamChatCompletion(
          client,
          settings.defaultModel,
          history,
          {
            onChunk(chunk) {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === 'assistant') {
                  updated[updated.length - 1] = {
                    ...last,
                    content: last.content + chunk,
                  };
                }
                return updated;
              });
            },
            onComplete() {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last && last.role === 'assistant') {
                  updated[updated.length - 1] = {
                    ...last,
                    streaming: false,
                  };
                }
                return updated;
              });
              setIsStreaming(false);
              abortRef.current = null;
              // Refocus input for follow-up
              setTimeout(() => inputRef.current?.focus(), 50);
            },
            onError(err) {
              if (err.message === 'Generation cancelled') {
                // Mark the partial response as complete on cancel
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = {
                      ...last,
                      content: last.content || '_(cancelled)_',
                      streaming: false,
                    };
                  }
                  return updated;
                });
              } else {
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last && last.role === 'assistant') {
                    updated[updated.length - 1] = {
                      ...last,
                      content: `_(Error: ${err.message})_`,
                      streaming: false,
                    };
                  }
                  return updated;
                });
              }
              setIsStreaming(false);
              abortRef.current = null;
            },
          },
          abort.signal,
        );
      } catch {
        // Errors are handled in onError above
      }
    },
    [
      isStreaming,
      isConfigured,
      rawContent,
      section,
      meta.title,
      messages,
      settings.apiKey,
      settings.defaultModel,
    ],
  );

  const handleCancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleCollapse = useCallback(() => {
    setExpanded(false);
  }, []);

  const handleAiButtonClick = useCallback(() => {
    if (!isConfigured) {
      setShowSettings(true);
      return;
    }
    if (!expanded) {
      setExpanded(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isConfigured, expanded]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit(inputValue);
      }
    },
    [handleSubmit, inputValue],
  );

  return (
    <>
      <div
        className={cn(
          'not-prose my-4 rounded-lg border border-border bg-card transition-all duration-200',
          expanded ? 'shadow-sm' : '',
        )}
      >
        {/* ── Collapsed / input row ──────────────────────────────────── */}
        <div className="flex items-center gap-2 p-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 shrink-0 gap-1.5 px-2"
            onClick={handleAiButtonClick}
            title={
              isConfigured
                ? 'Ask AI about this section'
                : 'Configure OpenRouter API key to use AI'
            }
          >
            <Sparkles className="h-4 w-4" />
            <span className="hidden text-xs sm:inline">AI</span>
          </Button>

          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (!isConfigured) {
                setShowSettings(true);
                return;
              }
            }}
            placeholder={`Ask about "${section}"…`}
            className="h-8 flex-1 border-0 bg-transparent text-sm shadow-none placeholder:text-muted-foreground/60 focus-visible:ring-0 focus-visible:ring-offset-0"
            disabled={isStreaming}
          />

          {/* Send / Cancel button */}
          {isStreaming ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 shrink-0 p-0"
              onClick={handleCancel}
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 shrink-0 p-0"
              onClick={() => handleSubmit(inputValue)}
              disabled={!inputValue.trim()}
              title="Send"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}

          {/* Collapse button — only shown when expanded */}
          {expanded && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 shrink-0 p-0"
              onClick={handleCollapse}
              title="Collapse"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* ── Chat thread ───────────────────────────────────────────── */}
        {expanded && messages.length > 0 && (
          <div className="border-t border-border">
            <ScrollArea
              ref={scrollAreaRef}
              className="max-h-[480px] overflow-y-auto px-3 py-3"
            >
              <div className="space-y-4">
                {messages.map((msg, i) => (
                  <ChatBubble key={`${msg.role}-${i}`} message={msg} />
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* AI settings dialog (opened when no key is configured) */}
      <AiSettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        apiKey={settings.apiKey}
        onApiKeyChange={setApiKey}
        promptTemplates={settings.promptTemplates}
        onPromptTemplateChange={setPromptTemplate}
        onResetPromptTemplates={resetPromptTemplates}
      />
    </>
  );
}

// ── Internal sub-components ───────────────────────────────────────────────────

interface ChatBubbleProps {
  message: Message;
}

function ChatBubble({ message }: ChatBubbleProps) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-3 py-2 text-sm',
          isUser
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-foreground',
        )}
      >
        {isUser ? (
          <span>{message.content}</span>
        ) : (
          <AssistantContent
            content={message.content}
            streaming={message.streaming}
          />
        )}
      </div>
    </div>
  );
}

interface AssistantContentProps {
  content: string;
  streaming?: boolean;
}

/**
 * Renders assistant message content with basic markdown-like formatting.
 * Handles **bold**, `code spans`, fenced code blocks, and newlines.
 */
function AssistantContent({ content, streaming }: AssistantContentProps) {
  if (!content && streaming) {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span className="text-xs">Thinking…</span>
      </span>
    );
  }

  // Split on fenced code blocks first
  const parts = content.split(/(```[\s\S]*?```)/g);

  return (
    <div className="space-y-2 leading-relaxed">
      {parts.map((part, i) => {
        if (part.startsWith('```')) {
          // Fenced code block
          const match = part.match(/^```(\w*)\n?([\s\S]*?)\n?```$/);
          const code = match ? match[2] : part.slice(3, -3);
          return (
            <pre
              key={`code-${i}`}
              className="overflow-x-auto rounded border border-border bg-background px-3 py-2 font-mono text-xs text-foreground"
            >
              <code>{code}</code>
            </pre>
          );
        }

        // Inline formatting: **bold**, `code`, newlines
        return <InlineContent key={`text-${i}`} text={part} />;
      })}
      {streaming && (
        <span className="ml-0.5 inline-block h-3 w-0.5 animate-pulse bg-current opacity-70" />
      )}
    </div>
  );
}

/**
 * Renders inline markdown tokens: **bold** and `code`.
 */
function InlineContent({ text }: { text: string }) {
  const tokens = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return (
    <span className="whitespace-pre-wrap">
      {tokens.map((token, i) => {
        if (token.startsWith('**') && token.endsWith('**')) {
          return <strong key={`bold-${i}`}>{token.slice(2, -2)}</strong>;
        }
        if (token.startsWith('`') && token.endsWith('`')) {
          return (
            <code
              key={`inline-${i}`}
              className="rounded border border-border bg-background px-1 py-0.5 font-mono text-xs"
            >
              {token.slice(1, -1)}
            </code>
          );
        }
        return token;
      })}
    </span>
  );
}
